import React, { useState, useRef } from 'react';
import { useApp } from '../../context/SupabaseAppContext';
import { 
  BellIcon, 
  Bars3Icon, 
  XMarkIcon,
  MagnifyingGlassIcon,
  UserCircleIcon,
  Cog6ToothIcon
} from '@heroicons/react/24/outline';
import { Menu, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faHome, 
  faLightbulb, 
  faRoute, 
  faTasks 
} from '@fortawesome/free-solid-svg-icons';

interface HeaderProps {
  currentPage: string;
  onPageChange: (page: string) => void;
}

const Header: React.FC<HeaderProps> = ({ currentPage, onPageChange }) => {
  const { state, actions } = useApp();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  
  const unreadNotifications = state.notifications.filter(n => !n.read).length;

  const navigation = [
    { name: 'Dashboard', key: 'dashboard', icon: faHome },
    { name: 'Ideas', key: 'ideas', icon: faLightbulb },
    { name: 'Tasks', key: 'tasks', icon: faTasks },
    { name: 'Roadmap', key: 'roadmap', icon: faRoute },
  ];

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    actions.setSearchQuery(e.target.value);
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (state.searchQuery.trim()) {
        // Navigate to search page with the query
        onPageChange('search');
      }
    } else if (e.key === 'Escape') {
      actions.setSearchQuery('');
      searchInputRef.current?.blur();
    }
  };

  return (
    <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo and Mobile Menu Button */}
          <div className="flex items-center">
            <div className="flex-shrink-0 flex items-center">
              <button 
                onClick={() => onPageChange('dashboard')}
                className="focus:outline-none"
              >
                <img 
                  src="/logo.jpg" 
                  alt="Idea Hopper Logo" 
                  className="h-[53px] w-[353px] object-contain hover:opacity-90 transition-opacity duration-200 -ml-16 cursor-pointer"
                />
              </button>
            </div>
            
            {/* Desktop Navigation */}
            <nav className="hidden md:-ml-10 md:flex md:space-x-8">
              {navigation.map((item) => (
                <button
                  key={item.key}
                  onClick={() => onPageChange(item.key)}
                  className={`inline-flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors duration-200 ${
                    currentPage === item.key
                      ? 'bg-deep-teal text-white'
                      : 'text-gray-700 hover:bg-ocean-mist hover:text-deep-teal'
                  }`}
                >
                  <FontAwesomeIcon icon={item.icon} className="mr-2 w-4 h-4" />
                  {item.name}
                </button>
              ))}
            </nav>
          </div>

          {/* Search Bar */}
          <div className="flex-1 max-w-md mx-8 hidden md:block">
            <div className="relative" ref={searchRef}>
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search ideas, tasks, roadmap... (Press Enter)"
                value={state.searchQuery}
                onChange={handleSearch}
                onKeyDown={handleSearchKeyDown}
                className="input-field pl-10 pr-4"
              />
            </div>
          </div>

          {/* Right side actions */}
          <div className="flex items-center space-x-4">
            {/* Notifications */}
            <button
              className="relative p-2 text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-deep-teal rounded-lg"
              onClick={() => onPageChange('notifications')}
            >
              <BellIcon className="h-6 w-6" />
              {unreadNotifications > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                  {unreadNotifications > 9 ? '9+' : unreadNotifications}
                </span>
              )}
            </button>

            {/* User Menu or Auth Button */}
            {state.currentUser ? (
              <Menu as="div" className="relative">
                <Menu.Button className="flex items-center space-x-3 text-sm focus:outline-none focus:ring-2 focus:ring-deep-teal rounded-lg p-2">
                  <div className="flex items-center space-x-2">
                    <span className="text-2xl">{state.currentUser.avatar}</span>
                    <div className="hidden md:block text-left">
                      <div className="text-sm font-medium text-gray-900">{state.currentUser.name}</div>
                      <div className="text-xs text-gray-500">Team Member</div>
                    </div>
                  </div>
                </Menu.Button>
                
                <Transition
                  as={Fragment}
                  enter="transition ease-out duration-100"
                  enterFrom="transform opacity-0 scale-95"
                  enterTo="transform opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="transform opacity-100 scale-100"
                  leaveTo="transform opacity-0 scale-95"
                >
                  <Menu.Items className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 ring-1 ring-black ring-opacity-5 focus:outline-none">
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          className={`${active ? 'bg-gray-100' : ''} flex items-center w-full px-4 py-2 text-sm text-gray-700`}
                          onClick={() => onPageChange('profile')}
                        >
                          <UserCircleIcon className="h-4 w-4 mr-3" />
                          Profile
                        </button>
                      )}
                    </Menu.Item>
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          className={`${active ? 'bg-gray-100' : ''} flex items-center w-full px-4 py-2 text-sm text-gray-700`}
                          onClick={() => onPageChange('settings')}
                        >
                          <Cog6ToothIcon className="h-4 w-4 mr-3" />
                          Settings
                        </button>
                      )}
                    </Menu.Item>
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          className={`${active ? 'bg-gray-100' : ''} flex items-center w-full px-4 py-2 text-sm text-gray-700 border-t border-gray-100`}
                          onClick={async () => {
                            try {
                              await actions.signOut();
                            } catch (error) {
                              console.error('Sign out error:', error);
                            }
                          }}
                        >
                          <svg className="h-4 w-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                          </svg>
                          Sign Out
                        </button>
                      )}
                    </Menu.Item>
                  </Menu.Items>
                </Transition>
              </Menu>
            ) : null}

            {/* Mobile menu button */}
            <button
              className="md:hidden p-2 text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-deep-teal rounded-lg"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? (
                <XMarkIcon className="h-6 w-6" />
              ) : (
                <Bars3Icon className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-gray-200 py-4">
            <div className="space-y-2">
              {navigation.map((item) => (
                <button
                  key={item.key}
                  onClick={() => {
                    onPageChange(item.key);
                    setMobileMenuOpen(false);
                  }}
                  className={`flex items-center w-full px-3 py-2 text-base font-medium rounded-lg transition-colors duration-200 ${
                    currentPage === item.key
                      ? 'bg-deep-teal text-white'
                      : 'text-gray-700 hover:bg-ocean-mist hover:text-deep-teal'
                  }`}
                >
                  <FontAwesomeIcon icon={item.icon} className="mr-3 w-5 h-5" />
                  {item.name}
                </button>
              ))}
            </div>
            
            {/* Mobile Search */}
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="Search... (Press Enter)"
                  value={state.searchQuery}
                  onChange={handleSearch}
                  onKeyDown={handleSearchKeyDown}
                  className="input-field pl-10 pr-4"
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header; 